import { getDefaultStore } from 'jotai';
import { authUserMetaAtom } from '@/store/authAtoms';
import { selectedFacilityAtom } from '@/store/facilityAtom';
import { siteGroupExperienceAtom } from '@/store/siteGroupExperienceAtom';
import {
  captureSelfRegPriorContext,
  EXPERIENCE_STORAGE_KEY,
  FACILITY_STORAGE_KEY,
  loadSelfRegSiteGroupEntry,
  readPriorFacilityForSelfRegLink,
  setSelfRegSiteGroupEntry,
  stashSelfRegPriorFacilityOnce,
} from '@/features/scheduling/lib/selfRegPriorContext';
import { lookupService } from '@/services/lookupService';
import { isDocumentPrerendering } from '@/lib/prerender';

/**
 * Self-Reg / ForceURLSiteGroup (`/Schedule/?SiteGroup=` → branding switch + D13 prior stash).
 *
 * ## Timing / prerender — DO NOT "simplify" back to a single module-load apply
 *
 * Observed failure (Walmart Self-Reg demos): user pastes
 * `http://localhost:5173/Schedule/?SiteGroup=PEI`, waits in the omnibox, hits Enter,
 * lands on `/self-reg` confirm — and the PEI theme never appears (still prior SiteGroup).
 * Quick Enter sometimes worked; long wait did not. Root cause is Chrome prerender:
 *
 * 1. Omnibox wait → Chrome prerenders Schedule+SiteGroup.
 * 2. We must NOT write `selected_facility` / experience during prerender (shared
 *    localStorage would re-theme the visible tab before Enter). See `lib/prerender.ts`.
 * 3. During prerender, React still runs and ProtectedRoute diverts to `/self-reg`,
 *    stripping `?SiteGroup=` from the URL.
 * 4. On Enter (activation), a bootstrap that only looks at `location.search` finds
 *    nothing → never switches to PEI.
 *
 * Fix (intentionally redundant — each path covers a race the others miss):
 *  - Remember link SiteGroup in sessionStorage + in-memory while prerendering.
 *  - `ensureSelfRegLinkSiteGroupApplied()` idempotently applies after activation
 *    from query **or** pending **or** entry key (even on `/self-reg`).
 *  - Call sites: module bootstrap, `prerenderingchange`, `SiteGroupUrlSync`,
 *    ProtectedRoute divert, SelfRegSchedule mount (after `waitUntilNotPrerendering`).
 *  - Clear stale experience + eager-fetch new experience so North palette cannot stick.
 *
 * Removing any one of those call sites tends to bring back intermittent demos.
 * Prefer extending `ensureSelfRegLinkSiteGroupApplied` over deleting callers.
 */
const PARAM_NAMES = ['sitegroup', 'siteGroup', 'SiteGroup'];

export const SELF_REG_PENDING_SITE_GROUP_KEY = 'pp_self_reg_pending_site_group';

/**
 * In-document fallback for the pending link SiteGroup.
 * sessionStorage from a prerender is only committed on activation; the in-memory
 * value survives within the same JS heap across prerender → activation.
 */
let pendingSiteGroupMemory: string | null = null;

function isSchedulePath(pathname: string): boolean {
  return /^\/schedule\/?$/i.test(pathname);
}

function isSelfRegPath(pathname: string): boolean {
  return /^\/self-reg\/?$/i.test(pathname);
}

function readSiteGroupFromSearch(search = window.location.search): { key: string; code: string } | null {
  const params = new URLSearchParams(search);
  const key = PARAM_NAMES.find((k) => params.has(k));
  if (!key) return null;
  const code = params.get(key)?.trim();
  if (!code) return null;
  return { key, code };
}

function rememberPendingSiteGroup(code: string): void {
  const trimmed = code.trim();
  if (!trimmed) return;
  pendingSiteGroupMemory = trimmed;
  try {
    sessionStorage.setItem(SELF_REG_PENDING_SITE_GROUP_KEY, trimmed);
  } catch { /* ignore */ }
  setSelfRegSiteGroupEntry(trimmed);
}

function readPendingSiteGroup(): string | null {
  const fromMemory = pendingSiteGroupMemory?.trim() || null;
  let fromSession: string | null = null;
  try {
    fromSession = sessionStorage.getItem(SELF_REG_PENDING_SITE_GROUP_KEY)?.trim() || null;
  } catch { /* ignore */ }
  return fromMemory || fromSession;
}

function clearPendingSiteGroup(): void {
  pendingSiteGroupMemory = null;
  try {
    sessionStorage.removeItem(SELF_REG_PENDING_SITE_GROUP_KEY);
  } catch { /* ignore */ }
}

function stripParamFromUrl(paramKey: string): void {
  const search = new URLSearchParams(window.location.search);
  if (!search.has(paramKey)) return;
  search.delete(paramKey);
  const newQuery = search.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (newQuery ? `?${newQuery}` : '') + window.location.hash,
  );
}

function clearStaleExperience(): void {
  const store = getDefaultStore();
  store.set(siteGroupExperienceAtom, null);
  try {
    localStorage.removeItem(EXPERIENCE_STORAGE_KEY);
  } catch { /* ignore */ }
}

/**
 * Resolve the Self-Reg link SiteGroup for the current navigation, if any.
 * Does not mutate storage.
 */
export function resolveSelfRegLinkSiteGroup(
  pathname = window.location.pathname,
  search = window.location.search,
): string | null {
  const fromSearch = readSiteGroupFromSearch(search);
  if (fromSearch && isSchedulePath(pathname)) return fromSearch.code;

  // After divert to /self-reg (query stripped) — pending / entry from this interrupt.
  if (isSelfRegPath(pathname) || isSchedulePath(pathname)) {
    return readPendingSiteGroup() || loadSelfRegSiteGroupEntry();
  }
  return null;
}

/**
 * Idempotent apply of Self-Reg link SiteGroup (facility + prior stash + theme).
 *
 * Safe from: module bootstrap, prerender activation, React route sync, SelfReg mount,
 * ProtectedRoute divert. Returns the SiteGroup applied, or null if nothing to do /
 * still prerendering (pending remembered only).
 *
 * Do not replace with "only run once at startup" — see file-level prerender notes.
 */
export function ensureSelfRegLinkSiteGroupApplied(): string | null {
  if (isDocumentPrerendering()) {
    // Remember only — never touch localStorage while prerendering (visible-tab poison).
    const fromSearch = readSiteGroupFromSearch();
    if (fromSearch && isSchedulePath(window.location.pathname)) {
      rememberPendingSiteGroup(fromSearch.code);
    }
    return null;
  }

  const pathname = window.location.pathname;
  const fromSearch = readSiteGroupFromSearch();
  const code =
    (fromSearch && isSchedulePath(pathname) ? fromSearch.code : null)
    || ((isSchedulePath(pathname) || isSelfRegPath(pathname))
      ? (readPendingSiteGroup() || loadSelfRegSiteGroupEntry())
      : null);

  // Unauthenticated non-schedule pages may still honor ?SiteGroup= (email deep links).
  const store = getDefaultStore();
  const authed = !!store.get(authUserMetaAtom);
  if (!code) {
    if (!authed && fromSearch) {
      // e.g. /home?SiteGroup=PEI pre-login
      const homeCode = fromSearch.code;
      const current = store.get(selectedFacilityAtom);
      if (!current || current.id.toLowerCase() !== homeCode.toLowerCase()) {
        const next = { id: homeCode, name: homeCode };
        store.set(selectedFacilityAtom, next);
        try {
          localStorage.setItem(FACILITY_STORAGE_KEY, JSON.stringify(next));
        } catch { /* ignore */ }
        clearStaleExperience();
      }
      sessionStorage.setItem('pp_facility_from_url_param', '1');
      stripParamFromUrl(fromSearch.key);
      return homeCode;
    }
    return null;
  }

  // Authenticated: only force-switch on Self-Reg interrupt paths.
  if (authed && !isSchedulePath(pathname) && !isSelfRegPath(pathname)) {
    return null;
  }

  const prior = readPriorFacilityForSelfRegLink(code);
  if (prior) {
    stashSelfRegPriorFacilityOnce(prior, code);
    captureSelfRegPriorContext({
      facility: prior,
      authMeta: store.get(authUserMetaAtom),
      returnPath: store.get(authUserMetaAtom) ? '/appointments' : '/home',
      linkSiteGroup: code,
    });
  }
  setSelfRegSiteGroupEntry(code);
  rememberPendingSiteGroup(code);

  const current = store.get(selectedFacilityAtom);
  const already = current && current.id.toLowerCase() === code.toLowerCase();
  if (!already) {
    const next = { id: code, name: code };
    store.set(selectedFacilityAtom, next);
    try {
      localStorage.setItem(FACILITY_STORAGE_KEY, JSON.stringify(next));
    } catch { /* ignore */ }
  }
  // Always drop prior palette when entering the link SiteGroup — even if facility was
  // already PEI from a poisoned/partial apply, experience may still be NorthEast.
  const exp = store.get(siteGroupExperienceAtom);
  if (!exp || exp.siteGroupCode.trim().toLowerCase() !== code.toLowerCase()) {
    clearStaleExperience();
  }

  sessionStorage.setItem('pp_facility_from_url_param', '1');
  clearPendingSiteGroup();
  // Keep entry key for Start Over; pending is cleared so we don't re-trigger oddly.
  setSelfRegSiteGroupEntry(code);

  if (fromSearch?.key) stripParamFromUrl(fromSearch.key);

  // Eager theme fetch — don't wait for React Query to notice the facility change.
  void lookupService.getSiteGroupExperience(code).then((experience) => {
    const latest = store.get(selectedFacilityAtom)?.id?.trim().toLowerCase();
    if (latest !== code.toLowerCase()) return;
    if (experience.siteGroupCode.trim().toLowerCase() !== code.toLowerCase()) return;
    store.set(siteGroupExperienceAtom, experience);
    try {
      localStorage.setItem(EXPERIENCE_STORAGE_KEY, JSON.stringify(experience));
    } catch { /* ignore */ }
  }).catch(() => { /* useSiteGroupExperience will retry */ });

  return code;
}

/**
 * Module-load entry. Defers localStorage writes while prerendering; applies on activation.
 * Not sufficient alone — React divert + stripped query require ensure callers after mount
 * (SiteGroupUrlSync / SelfRegSchedule). Keep the prerenderingchange listener anyway.
 */
export function bootstrapSiteGroupFromUrl(): void {
  if (isDocumentPrerendering()) {
    const fromSearch = readSiteGroupFromSearch();
    if (fromSearch && isSchedulePath(window.location.pathname)) {
      rememberPendingSiteGroup(fromSearch.code);
    }
    document.addEventListener(
      'prerenderingchange',
      () => {
        ensureSelfRegLinkSiteGroupApplied();
      },
      { once: true },
    );
    return;
  }
  ensureSelfRegLinkSiteGroupApplied();
}
